import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, FileText } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import { 
  FooterContent3, 
  CreateFooterContent3Data, 
  UpdateFooterContent3Data, 
  footerContent3Service 
} from "../../../services/footer/contact";

const FooterContent3Management: React.FC = () => {
  const [footerContent, setFooterContent] = useState<FooterContent3[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFooterContent, setEditingFooterContent] = useState<FooterContent3 | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchFooterContent = async () => {
    try {
      setLoading(true);
      const response = await footerContent3Service.getFooterContent3();
      setFooterContent(response || []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch footer content");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterContent();
  }, []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setEditingFooterContent(null);
    setIsModalOpen(false);
    setIsCreateMode(false);
  };

  const handleCreateFooterContent = async () => {
    if (!editingFooterContent) return;

    if (!editingFooterContent.title.trim() || 
        !editingFooterContent.description1.trim() || 
        !editingFooterContent.description2.trim()) {
      showError("All fields must be filled");
      return;
    }

    try {
      const createData: CreateFooterContent3Data = {
        title: editingFooterContent.title,
        description1: editingFooterContent.description1,
        description2: editingFooterContent.description2
      };

      await footerContent3Service.createFooterContent3(createData);
      await fetchFooterContent();
      showSuccess("Footer content created successfully");
      resetForm();
    } catch (err) {
      showError("Failed to create footer content");
    }
  };

  const handleUpdateFooterContent = async () => {
    if (!editingFooterContent) return;

    if (!editingFooterContent.title.trim() || 
        !editingFooterContent.description1.trim() || 
        !editingFooterContent.description2.trim()) {
      showError("All fields must be filled");
      return;
    }

    try {
      const updateData: UpdateFooterContent3Data = {
        id: editingFooterContent.id,
        title: editingFooterContent.title,
        description1: editingFooterContent.description1,
        description2: editingFooterContent.description2
      };

      await footerContent3Service.updateFooterContent3(updateData);
      await fetchFooterContent();
      showSuccess("Footer content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update footer content");
    }
  };

  const handleDeleteFooterContent = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this footer content?")) return;

    try {
      await footerContent3Service.deleteFooterContent3(id);
      await fetchFooterContent();
      showSuccess("Footer content deleted successfully");
    } catch (err) {
      showError("Failed to delete footer content");
    }
  };

  const filteredFooterContent = footerContent.filter((content) =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description1.toLowerCase().includes(searchTerm.toLowerCase()) ||
    content.description2.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
                  <FileText size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Footer Content 3 Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage contact information for footer</p>
                </div>
              </div>

              <button 
                onClick={() => {
                  setIsCreateMode(true);
                  setEditingFooterContent({
                    id: 0,
                    title: '',
                    description1: '',
                    description2: '',
                    created_at: '',
                    updated_at: ''
                  });
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Create New Content
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">TITLE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 hidden md:table-cell">DESCRIPTION 1</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 hidden md:table-cell">DESCRIPTION 2</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-36">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFooterContent.map((content, index) => (
                    <tr key={content.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {content.title}
                      </td>
                      <td className="px-8 py-6 text-gray-700 hidden md:table-cell max-w-md">
                        {content.description1.length > 100 
                          ? content.description1.substring(0, 100) + "..." 
                          : content.description1}
                      </td>
                      <td className="px-8 py-6 text-gray-700 hidden md:table-cell max-w-md">
                        {content.description2.length > 100 
                          ? content.description2.substring(0, 100) + "..." 
                          : content.description2}
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => {
                              setEditingFooterContent(content);
                              setIsCreateMode(false);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteFooterContent(content.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredFooterContent.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-20 text-center text-gray-400">
                        No footer content found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Create / Edit */}
      {isModalOpen && editingFooterContent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">
              {isCreateMode ? "Create Footer Content" : "Edit Footer Content"}
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={editingFooterContent.title}
                  onChange={(e) => setEditingFooterContent({ ...editingFooterContent, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-amber-500"
                  placeholder="Enter title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description 1</label>
                <textarea
                  value={editingFooterContent.description1}
                  onChange={(e) => setEditingFooterContent({ ...editingFooterContent, description1: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-amber-500 resize-y"
                  placeholder="Enter first description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description 2</label>
                <textarea
                  value={editingFooterContent.description2}
                  onChange={(e) => setEditingFooterContent({ ...editingFooterContent, description2: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-amber-500 resize-y"
                  placeholder="Enter second description..."
                />
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                onClick={resetForm}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={isCreateMode ? handleCreateFooterContent : handleUpdateFooterContent}
                className="flex-1 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl font-medium transition-all"
              >
                {isCreateMode ? "Create Content" : "Update Content"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterContent3Management;